import { create } from "zustand";

/**
 * Shared state for the AI Tutor surface (floating button + sheet + text
 * selection popup). Lives in its own store so the three components can
 * coordinate without prop-drilling, and so the rest of the app can open
 * the sheet from anywhere (e.g. dashboard mini-prompt, text-selection
 * popup inside viewers, etc.).
 */
type AITutorState = {
  isOpen: boolean;
  /**
   * Pinned chapter context. Normally the sheet reads chapter_id from
   * the URL params; on dashboard / cross-chapter surfaces we set this
   * to override so the chat still has a chapter to address.
   */
  pinnedChapterId: string | null;
  /**
   * Pre-filled textarea content. Used by the dashboard mini-prompt to
   * hand off whatever the user typed before the sheet existed.
   */
  pendingPrompt: string | null;
  /** Text-selection popup hands a reference here -- rendered as a badge. */
  pendingReference: string | null;

  open: (opts?: {
    reference?: string | null;
    prompt?: string | null;
    chapterId?: string | null;
  }) => void;
  close: () => void;
  setPendingReference: (ref: string | null) => void;

  /** Consumed by the sheet on first paint so the textarea pre-fills once. */
  consumePendingPrompt: () => string | null;
  /** Consumed by the sheet at send time, prepended to the message body. */
  consumePendingReference: () => string | null;
};

export const useAITutorStore = create<AITutorState>((set, get) => ({
  isOpen: false,
  pinnedChapterId: null,
  pendingPrompt: null,
  pendingReference: null,

  open: ({ reference, prompt, chapterId } = {}) =>
    set((s) => ({
      isOpen: true,
      pendingReference: reference !== undefined ? reference : s.pendingReference,
      pendingPrompt: prompt !== undefined ? prompt : s.pendingPrompt,
      pinnedChapterId: chapterId !== undefined ? chapterId : s.pinnedChapterId,
    })),
  close: () =>
    set({ isOpen: false, pinnedChapterId: null, pendingReference: null }),
  setPendingReference: (pendingReference) => set({ pendingReference }),

  consumePendingPrompt: () => {
    const p = get().pendingPrompt;
    if (p) set({ pendingPrompt: null });
    return p;
  },
  consumePendingReference: () => {
    const r = get().pendingReference;
    if (r) set({ pendingReference: null });
    return r;
  },
}));
